/**
 * Spacing, radii, elevation and stacking order (prompt.md §40 `design-tokens`).
 *
 * WHY everything here is plain numeric data: a spacing value is a *dp value* on
 * Android and a *px value* on web, and elevation on Android is a Material
 * tonal step while on web it is a box shadow. Expressing the intent as numbers
 * plus a small CSS adapter keeps the two platforms in step without either one
 * owning the source of truth.
 */

/** Spacing scale in density-independent pixels. */
export const SPACING = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  "2xl": 32,
  "3xl": 48,
  "4xl": 64,
} as const;

export type SpacingToken = keyof typeof SPACING;

export const RADII = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 20,
  pill: 9999,
  /** Half of the element's smaller side; renderers clamp this themselves. */
  full: 10000,
} as const;

export type RadiusToken = keyof typeof RADII;

export const Z_INDEX = {
  base: 0,
  sticky: 10,
  dropdown: 100,
  drawer: 200,
  modal: 300,
  toast: 400,
  tooltip: 500,
} as const;

export type ZIndexToken = keyof typeof Z_INDEX;

export interface ElevationToken {
  /** Ordinal kept for platforms that render elevation as a tonal step. */
  readonly level: number;
  readonly offsetY: number;
  readonly blur: number;
  readonly spread: number;
  /** Alpha of the shadow colour, per scheme. */
  readonly lightOpacity: number;
  readonly darkOpacity: number;
  readonly description: string;
}

export const ELEVATIONS: Readonly<Record<string, ElevationToken>> = {
  none: { level: 0, offsetY: 0, blur: 0, spread: 0, lightOpacity: 0, darkOpacity: 0, description: "flush with its container" },
  low: { level: 1, offsetY: 1, blur: 2, spread: 0, lightOpacity: 0.08, darkOpacity: 0.32, description: "cards and list rows" },
  medium: { level: 2, offsetY: 4, blur: 12, spread: -2, lightOpacity: 0.12, darkOpacity: 0.44, description: "menus, popovers, sticky headers" },
  high: { level: 3, offsetY: 12, blur: 32, spread: -6, lightOpacity: 0.18, darkOpacity: 0.56, description: "dialogs and sheets" },
  overlay: { level: 4, offsetY: 20, blur: 48, spread: -8, lightOpacity: 0.24, darkOpacity: 0.68, description: "framed overlays and onboarding surfaces" },
} as const;

export type ElevationName = keyof typeof ELEVATIONS;

/** CSS box shadow for an elevation step. Web-only adapter, no new semantics. */
export function cssBoxShadow(name: ElevationName, scheme: "light" | "dark"): string {
  const token = ELEVATIONS[name];
  if (!token) throw new TypeError(`unknown elevation "${name}"`);
  if (token.blur === 0 && token.offsetY === 0) return "none";
  const opacity = scheme === "dark" ? token.darkOpacity : token.lightOpacity;
  return `0 ${token.offsetY}px ${token.blur}px ${token.spread}px rgba(8, 11, 18, ${opacity})`;
}

/** CSS length for a spacing step. Web-only adapter. */
export function cssSpace(token: SpacingToken): string {
  return `${SPACING[token]}px`;
}

/** CSS length for a radius step. Web-only adapter. */
export function cssRadius(token: RadiusToken): string {
  if (token === "pill" || token === "full") return "9999px";
  return `${RADII[token]}px`;
}
